/* Linsoul quick view: native <dialog> around sections/ls-product-quickview.liquid.
   Opens from Minimog's .m-product-quickview-button (card eye icon, "Choose options"). */
(() => {
  'use strict';

  const cfg = window.LsQuickView || {};
  const routeRoot = window.Shopify?.routes?.root || '/';
  const pages = new Map();
  const assets = new Map();
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  let dialog;
  let body;
  let status;
  let trigger;
  let token = 0;
  let closing = 0;

  const once = (key, make) => {
    if (!assets.has(key)) assets.set(key, make());
    return assets.get(key);
  };
  const hasAsset = (selector, file) => [...document.querySelectorAll(selector)].some((el) => (el.href || el.src || '').includes(file));
  const css = (href) => once(href, () => new Promise((done) => {
    const file = new URL(href, location.href).pathname.split('/').pop();
    if (hasAsset('link[rel="stylesheet"]', file)) return done();
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = done;
    link.onerror = done;
    document.head.appendChild(link);
  }));
  const js = (src) => once(src, () => new Promise((done, fail) => {
    if (window.LsProduct) return done();
    const script = document.createElement('script');
    script.src = src;
    script.onload = done;
    script.onerror = fail;
    document.head.appendChild(script);
  }));

  const page = (handle) => {
    if (!pages.has(handle)) {
      const url = `${routeRoot}products/${encodeURIComponent(handle)}?section_id=ls-product-quickview`;
      pages.set(handle, fetch(url, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error(`Quick view ${r.status}`);
          return r.text();
        })
        .then((html) => {
          const node = new DOMParser().parseFromString(html, 'text/html').querySelector('.lsq');
          if (!node) throw new Error('Quick view markup missing');
          return node;
        })
        .catch((error) => {
          pages.delete(handle);
          throw error;
        }));
    }
    return pages.get(handle);
  };

  // Returns / warranty terms come from the product template's care section (its warranty profiles).
  const careData = new Map();
  const careSection = (box) => {
    const here = document.querySelector('[id^="shopify-section-template--"][id$="__ls_product_care"]');
    return here ? here.id.replace('shopify-section-', '') : box.dataset.lsqCareSection;
  };
  function fillCare(view, handle) {
    const box = view.querySelector('[data-lsq-care-section]');
    if (!box) return;
    const id = careSection(box);
    if (!id) return;
    if (!careData.has(handle)) {
      careData.set(handle, fetch(`${routeRoot}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(id)}`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ls-care-data]');
          return el ? JSON.parse(el.textContent).topics || {} : {};
        })
        .catch(() => ({})));
    }
    careData.get(handle).then((topics) => {
      box.querySelectorAll('[data-lsq-care]').forEach((row) => {
        const topic = topics[row.dataset.lsqCare];
        if (topic) {
          const title = row.querySelector('[data-lsq-care-title]');
          const summary = row.querySelector('[data-lsq-care-summary]');
          if (title && topic.title) title.textContent = topic.title;
          if (summary && topic.summary) summary.textContent = topic.summary;
          if (topic.url) row.href = topic.url;
        }
        row.removeAttribute('data-pending');
      });
    });
  }

  const icon = (name, fallback) => (window.LsIcons?.has?.(name) ? window.LsIcons.svg(name) : fallback);

  const skeleton = () => `
    <div class="lsq-skeleton" aria-hidden="true">
      <div class="lsq-sk-media"></div>
      <div class="lsq-sk-info"><i style="width:34%"></i><i class="lsq-sk-title"></i><i style="width:72%"></i><i class="lsq-sk-price"></i><i style="width:58%"></i><i class="lsq-sk-btn"></i></div>
    </div>`;

  function build() {
    if (dialog) return;
    dialog = document.createElement('dialog');
    dialog.className = 'lsq-dialog';
    dialog.setAttribute('aria-label', cfg.dialogLabel || '');
    dialog.innerHTML = `
      <div class="lsq-panel">
        <button class="lsq-close" type="button" data-lsq-close aria-label="${cfg.closeLabel || ''}">${icon('close', '&times;')}</button>
        <p class="lsq-sr" role="status" data-lsq-status></p>
        <div class="lsq-body" data-lsq-body></div>
      </div>`;
    document.body.appendChild(dialog);
    body = dialog.querySelector('[data-lsq-body]');
    status = dialog.querySelector('[data-lsq-status]');

    dialog.querySelector('[data-lsq-close]').addEventListener('click', () => close());
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      close();
    });
    // Backdrop: the dialog box itself is transparent, the panel is the visible card.
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) close();
    });
    dialog.addEventListener('close', () => {
      if (dialog.open) return;
      document.documentElement.classList.remove('lsq-lock');
      dialog.classList.remove('is-open', 'is-closing');
      body.replaceChildren();
      if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
    });
  }

  function close(instant) {
    if (!dialog?.open) return;
    token += 1;
    clearTimeout(closing);
    if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      dialog.close();
      return;
    }
    dialog.classList.add('is-closing');
    dialog.classList.remove('is-open');
    closing = setTimeout(() => dialog.open && dialog.close(), 200);
  }

  async function open(button) {
    const handle = button.dataset.productHandle;
    if (!handle) return;
    const fallback = button.dataset.productUrl || `${routeRoot}products/${handle}`;
    await css(cfg.quickCss);
    build();
    trigger = button;
    const mine = ++token;
    clearTimeout(closing);

    body.innerHTML = skeleton();
    dialog.setAttribute('aria-busy', 'true');
    status.textContent = cfg.loadingLabel || '';
    if (!dialog.open) {
      dialog.classList.remove('is-closing');
      dialog.showModal();
      document.documentElement.classList.add('lsq-lock');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (mine === token && dialog.open) dialog.classList.add('is-open');
      }));
    } else {
      dialog.classList.remove('is-closing');
      dialog.classList.add('is-open');
    }

    try {
      const [node] = await Promise.all([page(handle), css(cfg.productCss), js(cfg.productJs)]);
      if (mine !== token || !dialog.open) return;
      const view = document.importNode(node, true);
      body.replaceChildren(view);
      dialog.removeAttribute('aria-busy');
      status.textContent = '';
      const title = view.querySelector('.lsq-title');
      if (title?.id) {
        dialog.removeAttribute('aria-label');
        dialog.setAttribute('aria-labelledby', title.id);
      }
      fillCare(view, handle);
      window.LsProduct?.init();
      window.Shopify?.PaymentButton?.init?.();
      document.dispatchEvent(new CustomEvent('quick-view:loaded', { detail: { productUrl: handle } }));
    } catch (error) {
      console.warn(error);
      if (mine === token) window.location.href = fallback;
    }
  }

  document.addEventListener('click', (e) => {
    const button = e.target.closest?.('.m-product-quickview-button');
    if (!button || button.classList.contains('disabled')) return;
    e.preventDefault();
    e.stopPropagation();
    open(button);
  }, true);

  // Start the fetch while the pointer is on its way to clicking.
  const warm = (e) => {
    const button = e.target.closest?.('.m-product-quickview-button');
    if (!button?.dataset.productHandle) return;
    page(button.dataset.productHandle).catch(() => {});
    css(cfg.productCss);
  };
  document.addEventListener('pointerover', (e) => { if (fine.matches) warm(e); }, { passive: true });
  document.addEventListener('focusin', warm);

  document.addEventListener('product-ajax:added', () => close(true));

  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
  idle(() => css(cfg.quickCss));
})();
